/**
 * VeyroCam — Cart
 *
 * AJAX add / update / remove against Shopify's Cart API, with the drawer and
 * cart page re-rendered from the server after every mutation.
 *
 * Why re-render server-side
 * -------------------------
 * Totals, discount allocations and free-shipping thresholds are all computed
 * by Shopify. Recomputing them in JavaScript would eventually disagree with
 * checkout — the one discrepancy a store cannot afford. So the script sends
 * the mutation, then asks Shopify to re-render the cart sections and swaps
 * the markup in.
 *
 * Every path degrades: the product form posts to /cart/add, quantity inputs
 * post `updates[]` to /cart, and remove links are real hrefs to
 * /cart/change. With JavaScript unavailable all of it still works.
 */

(function () {
  'use strict';

  var DRAWER_ID = 'VeyroCartDrawer';
  var SECTION_CART_DRAWER = 'cart-drawer';
  var DEBOUNCE_MS = 400;

  /* =====================================================================
     Helpers
     ===================================================================== */

  function announce(message) {
    var region = document.getElementById('VeyroLiveRegion');
    if (region) region.textContent = message;
  }

  function drawer() {
    return document.getElementById(DRAWER_ID);
  }

  function setBusy(state) {
    var panel = document.querySelector('[data-cart-drawer-contents]');
    if (panel) panel.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  /**
   * Refresh the cart count badge in the header. Read from the authoritative
   * /cart.js payload rather than counted in the DOM.
   */
  function updateCount(count) {
    document.querySelectorAll('.vc-cart-count').forEach(function (node) {
      node.textContent = count;
      node.dataset.count = count;
    });

    var drawerCount = document.querySelector('[data-cart-count]');
    if (drawerCount) drawerCount.textContent = '(' + count + ')';
  }

  /**
   * Re-render the drawer from the server and swap its contents in.
   * Resolves once the DOM is updated.
   */
  function refreshDrawer() {
    var host = document.querySelector('[data-cart-drawer-contents]');
    if (!host) return Promise.resolve();

    return fetch('?section_id=' + SECTION_CART_DRAWER)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-contents]');
        if (fresh) host.innerHTML = fresh.innerHTML;
      })
      .catch(function () {
        /* Leave the current markup in place; the next navigation corrects it. */
      });
  }

  /** Reload the cart page's own markup after a line change. */
  function refreshCartPage() {
    var form = document.querySelector('[data-cart-form]');
    if (!form) return Promise.resolve();

    return fetch(window.location.pathname + '?section_id=main-cart')
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('.vc-cart-section') || doc.querySelector('section');
        var live = document.querySelector('.vc-cart-section');
        if (fresh && live) live.innerHTML = fresh.innerHTML;
      })
      .catch(function () {
        window.location.reload();
      });
  }

  function syncState() {
    return fetch(window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js')
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        updateCount(cart.item_count);
        return cart;
      })
      .catch(function () {
        return null;
      });
  }

  /* =====================================================================
     Add to cart
     ===================================================================== */

  function bindAddToCart() {
    var form = document.getElementById('ProductForm');
    if (!form) return;

    form.addEventListener('submit', function (event) {
      // Let the accelerated-checkout buttons submit natively — they take the
      // shopper straight to checkout and must not be intercepted.
      if (event.submitter && event.submitter.name !== 'add') return;

      event.preventDefault();

      var button = form.querySelector('[data-add-to-cart]');
      var sticky = document.querySelector('[data-sticky-add]');

      [button, sticky].forEach(function (el) {
        if (el) el.classList.add('vc-btn--loading');
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (response) {
          return response.json().then(function (data) {
            if (!response.ok) throw data;
            return data;
          });
        })
        .then(function (item) {
          announce(item.product_title + ' — ' + (form.dataset.addedText || 'ajouté au panier'));
          return Promise.all([syncState(), refreshDrawer()]);
        })
        .then(function () {
          var panel = drawer();
          if (panel && panel.open) panel.open();
        })
        .catch(function (error) {
          // Shopify returns a human-readable `description` for inventory and
          // validation failures — surface it rather than a generic message.
          var message =
            (error && (error.description || error.message)) ||
            form.dataset.errorText ||
            'Une erreur est survenue.';
          announce(message);
          showFormError(form, message);
        })
        .finally(function () {
          [button, sticky].forEach(function (el) {
            if (el) el.classList.remove('vc-btn--loading');
          });
        });
    });
  }

  function showFormError(form, message) {
    var existing = form.querySelector('[data-cart-error]');
    if (existing) existing.remove();

    var node = document.createElement('p');
    node.className = 'vc-form-message vc-form-message--error';
    node.setAttribute('data-cart-error', '');
    node.setAttribute('role', 'alert');
    node.textContent = message;

    var actions = form.querySelector('.vc-product__actions');
    (actions || form).appendChild(node);
  }

  /* =====================================================================
     Line changes (quantity + removal)
     ===================================================================== */

  function changeLine(line, quantity) {
    setBusy(true);

    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        updateCount(cart.item_count);
        return Promise.all([refreshDrawer(), refreshCartPage()]);
      })
      .catch(function () {
        // Cart mutations must never silently no-op: a stale quantity is worse
        // than a reload.
        window.location.reload();
      })
      .finally(function () {
        setBusy(false);
      });
  }

  function bindLineChanges() {
    var debounceTimer = null;

    document.addEventListener('change', function (event) {
      var input = event.target.closest('[data-quantity-input]');
      if (!input) return;

      var line = parseInt(input.dataset.lineIndex, 10);
      var quantity = Math.max(0, parseInt(input.value, 10) || 0);

      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(function () {
        changeLine(line, quantity);
      }, DEBOUNCE_MS);
    });

    document.addEventListener('click', function (event) {
      var remove = event.target.closest('[data-remove-line]');
      if (!remove) return;

      event.preventDefault();
      changeLine(parseInt(remove.dataset.lineIndex, 10), 0);
    });

    // Quantity steppers inside cart lines. The product page binds its own in
    // veyro-product.js; this covers lines rendered into the drawer, which the
    // product script never sees.
    document.addEventListener('click', function (event) {
      var button = event.target.closest('[data-qty-up], [data-qty-down]');
      if (!button) return;

      var wrapper = button.closest('[data-vc-qty]');
      if (!wrapper) return;

      var input = wrapper.querySelector('[data-quantity-input]');
      if (!input) return;   // product-page stepper, handled elsewhere

      event.preventDefault();

      var delta = button.hasAttribute('data-qty-up') ? 1 : -1;
      var min = parseInt(input.min, 10) || 0;
      input.value = Math.max(min, (parseInt(input.value, 10) || 0) + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* =====================================================================
     Header cart button opens the drawer
     ===================================================================== */

  function bindCartTrigger() {
    document.addEventListener('click', function (event) {
      var trigger = event.target.closest('a[href$="/cart"], a[href*="/cart?"]');
      if (!trigger) return;
      if (!drawer()) return;

      // Modified clicks are the shopper deliberately opening a new tab.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      // On the cart page itself, the link is a no-op — let it behave natively.
      if (window.location.pathname.indexOf('/cart') !== -1) return;

      event.preventDefault();
      drawer().open(trigger);
    });
  }

  function init() {
    bindAddToCart();
    bindLineChanges();
    bindCartTrigger();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
